/**
 * Przeszkody na dachu w osiach połaci: komin, okno dachowe, wyłaz, antena, wentylacja, inna przeszkoda,
 * pasy (ława i stopnie kominiarskie, płotki i kratki przeciwśniegowe), lukarny i części połaci pod wyższym
 * dachem innego skrzydła.
 *
 * Umowa z rysunkiem rzutu dachu (2026-10-23): każda przeszkoda ma stały klucz `rodzaj` i polską `nazwa`
 * do legendy; `obrys` to jej kształt na płaszczyźnie połaci, a `strefa` to obrys z zapasem, czyli dokładnie
 * wielokąt, który wycina moduły (`panele.js` dostaje te same punkty jako `strefaNaDachu`). Oba to wielokąty
 * wypukłe [[u, v]...] przeciwnie do wskazówek zegara, w metrach od `polac.poczatek` wzdłuż `polac.u`
 * i `polac.v` (jak `uvPolaci`), przycięte do obrysu połaci.
 *
 * Przeszkody stojące (komin, antena, wentylacja, inna) to pionowe graniastosłupy obrócone wokół pionu jak
 * rotation.y: ich ślad na połaci to rzut pionowy podstawy na płaszczyznę dachu, więc na stromym dachu
 * wydłuża się wzdłuż spadku (1 / cos kąta). Leżące w połaci (okno dachowe, wyłaz) mają wymiary i obrót
 * w płaszczyźnie połaci: szerokość wzdłuż okapu, długość wzdłuż spadku, obrót 0 to krawędzie równoległe
 * do okapu, dodatni obrót od u w stronę v.
 *
 * Pasy biegną w osiach połaci od klikniętego punktu: „w górę dachu” do kalenicy (albo na podaną długość),
 * „wzdłuż okapu” przez całą szerokość połaci (albo na podaną długość, środkiem w punkcie). Lukarna to bryła
 * stojąca na połaci ze ścianą czołową w klikniętym punkcie (środek jej dolnej krawędzi); jej ślad sięga od
 * okapu daszka z przodu do miejsca, w którym daszek wchodzi w dach.
 */
import { dodaj, skaluj, normalizuj, uvPolaci } from './bryla'
import { typElementu } from './projekt'

const RAD = Math.PI / 180
const EPS = 1e-9
// Wyższy dach innego skrzydła liczy się dopiero od 2 cm nad połacią (styk płaszczyzn to nie zakrycie).
const PROG_ZAKRYCIA = 0.02

export const PRZESZKODY_STOJACE = ['komin', 'antena', 'wentylacja', 'inna']
export const PRZESZKODY_LEZACE = ['okno_dachowe', 'wylaz']
export const PRZESZKODY_PASY = ['lawa_kominiarska', 'stopnie_kominiarskie', 'plotek_sniegowy', 'kratka_sniegowa']
// Okap daszka lukarny z przodu, m.
const OKAP_LUKARNY = 0.15

const NAZWY = {
  komin: 'Komin',
  okno_dachowe: 'Okno dachowe',
  wylaz: 'Wyłaz dachowy',
  antena: 'Antena',
  wentylacja: 'Wentylacja',
  inna: 'Przeszkoda',
  lawa_kominiarska: 'Ława kominiarska',
  stopnie_kominiarskie: 'Stopnie kominiarskie',
  plotek_sniegowy: 'Płotek przeciwśniegowy',
  kratka_sniegowa: 'Kratka przeciwśniegowa',
  lukarna: 'Lukarna',
  pod_dachem: 'Pod dachem innej części budynku',
}

/** Nazwa przeszkody do legendy rysunku, w liczbie pojedynczej. */
export const nazwaPrzeszkody = (rodzaj) => NAZWY[rodzaj] || 'Przeszkoda'

/* ═══ Geometria 2D ══════════════════════════════════════════════════════════ */

const pole2D = (w) => {
  let s = 0
  for (let i = 0; i < w.length; i++) {
    const a = w[i]
    const b = w[(i + 1) % w.length]
    s += a[0] * b[1] - b[0] * a[1]
  }
  return s / 2
}

const przeciwnieDoZegara = (w) => (pole2D(w) < 0 ? [...w].reverse() : w)

export const wewnatrz2d = (x, z, w) => {
  let wyn = false
  for (let i = 0, j = w.length - 1; i < w.length; j = i, i += 1) {
    const [xi, zi] = w[i]; const [xj, zj] = w[j]
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) wyn = !wyn
  }
  return wyn
}

// Część wielokąta po stronie nx·x + ny·y ≥ c (Sutherland–Hodgman).
const przytnijPolplaszczyzna = (w, nx, ny, c) => {
  const wynik = []
  for (let i = 0; i < w.length; i++) {
    const a = w[i]
    const b = w[(i + 1) % w.length]
    const da = nx * a[0] + ny * a[1] - c
    const db = nx * b[0] + ny * b[1] - c
    if (da >= 0) wynik.push(a)
    if ((da >= 0) !== (db >= 0)) {
      const t = da / (da - db)
      wynik.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])
    }
  }
  return wynik.length >= 3 ? wynik : []
}

/** Część wspólna wielokąta i wielokąta wypukłego (oba dowolnie zorientowane); pusta tablica, gdy brak. */
export const przytnijDoWypuklego = (w, wypukly) => {
  const k = przeciwnieDoZegara(wypukly)
  let wynik = przeciwnieDoZegara(w)
  for (let i = 0; i < k.length && wynik.length; i++) {
    const a = k[i]
    const b = k[(i + 1) % k.length]
    const dl = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (dl < EPS) continue
    const nx = -(b[1] - a[1]) / dl
    const ny = (b[0] - a[0]) / dl
    wynik = przytnijPolplaszczyzna(wynik, nx, ny, nx * a[0] + ny * a[1])
  }
  return Math.abs(pole2D(wynik)) > 1e-9 ? wynik : []
}

/**
 * Wielokąt wypukły odsunięty na zewnątrz o `zapas` (każdy bok równolegle, narożniki ostre), przeciwnie
 * do wskazówek zegara. Prostokąt rośnie o zapas z każdej strony, jak dawny prostokąt przeszkody.
 */
export const odsunWielokat = (w, zapas) => {
  const k = przeciwnieDoZegara(w).filter((p, i, t) => Math.hypot(p[0] - t[(i + 1) % t.length][0], p[1] - t[(i + 1) % t.length][1]) > 1e-9)
  if (!zapas || k.length < 3) return k
  const linie = k.map((a, i) => {
    const b = k[(i + 1) % k.length]
    const dl = Math.hypot(b[0] - a[0], b[1] - a[1])
    const n = [(b[1] - a[1]) / dl, -(b[0] - a[0]) / dl]
    return { n, c: n[0] * a[0] + n[1] * a[1] + zapas }
  })
  return linie.map((l2, i) => {
    const l1 = linie[(i + linie.length - 1) % linie.length]
    const wyz = l1.n[0] * l2.n[1] - l1.n[1] * l2.n[0]
    if (Math.abs(wyz) < 1e-12) {
      const p = k[i]
      return [p[0] + l2.n[0] * zapas, p[1] + l2.n[1] * zapas]
    }
    return [(l1.c * l2.n[1] - l2.c * l1.n[1]) / wyz, (l1.n[0] * l2.c - l2.n[0] * l1.c) / wyz]
  })
}

/* ═══ Połać i dach ══════════════════════════════════════════════════════════ */

const rzutPolaci = (p) => p.wierzcholki.map((v) => [v[0], v[2]])

// Wysokość płaszczyzny połaci nad punktem (x, z) albo null dla płaszczyzny pionowej.
const yPlaszczyzny = (p, x, z) => {
  const [nx, ny, nz] = p.normalna
  if (Math.abs(ny) < 1e-6) return null
  const p0 = p.wierzcholki[0]
  return p0[1] - (nx * (x - p0[0]) + nz * (z - p0[2])) / ny
}

/** Wysokość dachu w punkcie (x, z): najwyższa połać, której rzut zawiera punkt; null poza dachem. */
export const wysokoscDachu = (bryla, x, z) => {
  let maks = null
  for (const p of bryla.polacie || []) {
    if (!wewnatrz2d(x, z, rzutPolaci(p))) continue
    const y = yPlaszczyzny(p, x, z)
    if (y != null && (maks === null || y > maks)) maks = y
  }
  return maks
}

/** Połać pod punktem (x, z), jeśli jest (przy kilku najwyższa). */
export const polacPod = (bryla, x, z) => {
  let wybrana = null
  let maks = -Infinity
  for (const p of bryla.polacie || []) {
    if (!wewnatrz2d(x, z, rzutPolaci(p))) continue
    const y = yPlaszczyzny(p, x, z)
    if (y != null && y > maks) { maks = y; wybrana = p }
  }
  return wybrana
}

/** Punkt połaci nad (x, z): rzut pionowy na jej płaszczyznę. */
export const naPlaszczyznie = (p, x, z) => [x, yPlaszczyzny(p, x, z) ?? p.wierzcholki[0][1], z]

/** Punkt świata z (u, v) połaci (na jej płaszczyźnie). */
export const punktPolaci = (p, u, v) => dodaj(p.poczatek, dodaj(skaluj(p.u, u), skaluj(p.v, v)))

/** Obrys połaci w jej osiach [[u, v]...], przeciwnie do wskazówek zegara. */
export const obrysPolaciUv = (p) => przeciwnieDoZegara(p.wierzcholki.map((w) => uvPolaci(p, w)))

/* ═══ Ślady przeszkód ═══════════════════════════════════════════════════════ */

/** Parametry elementu z wartościami domyślnymi jego typu. */
export const parametryElementu = (el) => ({ ...(typElementu(el.typ)?.domyslne || {}), ...(el.parametry || {}) })

// Narożniki poziomej podstawy przeszkody stojącej [[x, z]...] (obrót jak rotation.y).
const podstawaStojacej = (el) => {
  const d = parametryElementu(el)
  const a = (el.obrot || 0) * RAD
  const ex = [Math.cos(a), -Math.sin(a)]
  const ez = [Math.sin(a), Math.cos(a)]
  const sx = (d.szerokosc ?? 0.5) / 2
  const sz = (d.glebokosc ?? 0.5) / 2
  const [x, , z] = el.pozycja
  return [[-sx, -sz], [sx, -sz], [sx, sz], [-sx, sz]].map(([p, q]) => [x + ex[0] * p + ez[0] * q, z + ex[1] * p + ez[1] * q])
}

// Ślad przeszkody leżącej w połaci w jej osiach (prostokąt obrócony w płaszczyźnie).
const sladLezacej = (el, p) => {
  const d = parametryElementu(el)
  const [uc, vc] = uvPolaci(p, naPlaszczyznie(p, el.pozycja[0], el.pozycja[2]))
  const a = (el.obrot || 0) * RAD
  const c = Math.cos(a)
  const s = Math.sin(a)
  const su = (d.szerokosc ?? 0.6) / 2
  const sv = (d.dlugosc ?? 0.8) / 2
  return [[-su, -sv], [su, -sv], [su, sv], [-su, sv]].map(([du, dv]) => [uc + du * c - dv * s, vc + du * s + dv * c])
}

// Ślad pasa w osiach połaci przed przycięciem do połaci („do krawędzi” to bardzo długi prostokąt).
const sladPasa = (el, p) => {
  const d = parametryElementu(el)
  const [uc, vc] = uvPolaci(p, naPlaszczyznie(p, el.pozycja[0], el.pozycja[2]))
  const w = Math.max(0.02, d.szerokosc ?? 0.3) / 2
  const L = d.doKrawedzi !== false ? 1e3 : Math.max(0.1, d.dlugosc ?? 3)
  if (d.kierunek === 'okap') return [[uc - L / 2, vc - w], [uc + L / 2, vc - w], [uc + L / 2, vc + w], [uc - L / 2, vc + w]]
  return [[uc - w, vc], [uc + w, vc], [uc + w, vc + L], [uc - w, vc + L]]
}

/**
 * Bryła lukarny na połaci pod punktem `el.pozycja` (środek dolnej krawędzi ściany czołowej) albo null
 * (poza dachem albo na połaci prawie płaskiej). Parametry: szerokość ściany czołowej, wysokość ściany
 * czołowej nad dachem, dach 'dwuspadowy' (kąt daszka) albo 'pulpitowy' (daszek opada w stronę okapu
 * łagodniej niż dach, kąt co najmniej 5° mniejszy od kąta połaci).
 * @returns { polac, sciany: [[[x, y, z]...]...], dachy, okno: { srodek, szerokosc, wysokosc }, slad: [[u, v]...] }
 */
export const brylaLukarny = (bryla, el) => {
  const p = polacPod(bryla, el.pozycja[0], el.pozycja[2])
  if (!p || p.nachylenie < 10) return null
  const d = parametryElementu(el)
  const ta = Math.tan(p.nachylenie * RAD)
  const F = naPlaszczyznie(p, el.pozycja[0], el.pozycja[2])
  const u = normalizuj([p.u[0], 0, p.u[2]])
  const h = normalizuj([p.v[0], 0, p.v[2]])
  const w = Math.max(0.6, d.szerokosc ?? 2.2) / 2
  const H = Math.max(0.4, d.wysokosc ?? 1.4)
  const o = OKAP_LUKARNY
  // (a wzdłuż okapu, s poziomo w głąb dachu od ściany czołowej, y nad dolną krawędzią ściany).
  const pt = (a, s, y) => [F[0] + u[0] * a + h[0] * s, F[1] + y, F[2] + u[2] * a + h[2] * s]
  let sciany
  let dachy
  let sMax
  if (d.dach === 'pulpitowy') {
    const tb = Math.tan(Math.max(0, Math.min(d.kat ?? 10, p.nachylenie - 5)) * RAD)
    const sP = H / (ta - tb)
    const yP = H + sP * tb
    sciany = [
      [pt(-w, 0, 0), pt(w, 0, 0), pt(w, 0, H), pt(-w, 0, H)],
      [pt(w, 0, 0), pt(w, sP, yP), pt(w, 0, H)],
      [pt(-w, 0, 0), pt(-w, 0, H), pt(-w, sP, yP)],
    ]
    dachy = [[pt(-w, -o, H - o * tb), pt(w, -o, H - o * tb), pt(w, sP, yP), pt(-w, sP, yP)]]
    sMax = sP
  } else {
    const tb = Math.tan(Math.min(60, Math.max(10, d.kat ?? 35)) * RAD)
    const yR = H + w * tb
    const sE = H / ta
    const sR = yR / ta
    sciany = [
      [pt(-w, 0, 0), pt(w, 0, 0), pt(w, 0, H), pt(0, 0, yR), pt(-w, 0, H)],
      [pt(w, 0, 0), pt(w, sE, H), pt(w, 0, H)],
      [pt(-w, 0, 0), pt(-w, 0, H), pt(-w, sE, H)],
    ]
    dachy = [
      [pt(w, -o, H), pt(w, sE, H), pt(0, sR, yR), pt(0, -o, yR)],
      [pt(-w, -o, H), pt(0, -o, yR), pt(0, sR, yR), pt(-w, sE, H)],
    ]
    sMax = sR
  }
  const szerOkna = Math.min(2 * w - 0.5, 1.2)
  const wysOkna = Math.min(H - 0.35, 1.2)
  const okno = szerOkna > 0.3 && wysOkna > 0.3
    ? { srodek: pt(0, -0.02, 0.25 + wysOkna / 2), szerokosc: szerOkna, wysokosc: wysOkna, normalna: skaluj(h, -1), wzdluz: u }
    : null
  const [Fu, Fv] = uvPolaci(p, F)
  const ca = Math.cos(p.nachylenie * RAD)
  // Szerokość śladu wzdłuż u połaci (u w połaci skośnej jest poziome, jak `u` lukarny).
  const slad = [[Fu - w, Fv - o / ca], [Fu + w, Fv - o / ca], [Fu + w, Fv + sMax / ca], [Fu - w, Fv + sMax / ca]]
  return { polac: p, sciany, dachy, okno, slad }
}

const dlaPolaci = (p, rodzaj, id, elementId, obrys, zapas, wysokosc) => {
  const polacUv = obrysPolaciUv(p)
  const o = przytnijDoWypuklego(obrys, polacUv)
  const s = przytnijDoWypuklego(odsunWielokat(obrys, zapas), polacUv)
  if (!s.length) return null
  return {
    id, elementId, rodzaj, nazwa: nazwaPrzeszkody(rodzaj), polacId: p.id, obrys: o, strefa: s, zapas, wysokosc,
    strefaNaDachu: s.map(([u, v]) => punktPolaci(p, u, v)),
  }
}

/**
 * Przeszkody elementów (`projekt.elementy`) na połaci `polac`. Leżące należą do połaci pod swoim środkiem,
 * stojące do każdej połaci tego samego skrzydła, na którą wchodzi ich podstawa (komin na kalenicy wycina
 * moduły po obu stronach).
 */
const przeszkodyElementow = (bryla, elementy, polac) => {
  const wynik = []
  const rzut = rzutPolaci(polac)
  for (const el of elementy || []) {
    if (!el?.pozycja) continue
    const rodzaj = el.typ
    const d = parametryElementu(el)
    const zapas = Math.max(0, d.zapas ?? 0.2)
    const pod = polacPod(bryla, el.pozycja[0], el.pozycja[2])
    if (!pod) continue
    if (PRZESZKODY_LEZACE.includes(rodzaj)) {
      if (pod.id !== polac.id) continue
      const x = dlaPolaci(polac, rodzaj, el.id, el.id, sladLezacej(el, polac), zapas, 0)
      if (x) wynik.push(x)
    } else if (PRZESZKODY_STOJACE.includes(rodzaj)) {
      if (pod.skrzydloId !== polac.skrzydloId) continue
      const podstawa = podstawaStojacej(el)
      if (!przytnijDoWypuklego(podstawa, rzut).length) continue
      const obrys = podstawa.map(([x, z]) => uvPolaci(polac, naPlaszczyznie(polac, x, z)))
      const wys = rodzaj === 'komin' ? (d.ponadDach ?? 1) : (d.wysokosc ?? 0)
      const x = dlaPolaci(polac, rodzaj, el.id, el.id, obrys, zapas, wys)
      if (x) wynik.push(x)
    } else if (PRZESZKODY_PASY.includes(rodzaj)) {
      if (pod.id !== polac.id) continue
      const x = dlaPolaci(polac, rodzaj, el.id, el.id, sladPasa(el, polac), zapas, 0)
      if (x) wynik.push(x)
    } else if (rodzaj === 'lukarna') {
      if (pod.id !== polac.id) continue
      const l = brylaLukarny(bryla, el)
      if (!l) continue
      const x = dlaPolaci(polac, rodzaj, el.id, el.id, l.slad, zapas, Math.max(...[...l.sciany, ...l.dachy].flat().map((q) => q[1])) - l.sciany[0][0][1])
      if (x) wynik.push(x)
    }
  }
  return wynik
}

/**
 * Części połaci pod wyższym dachem innego skrzydła (rzuty obu połaci się nakładają, a tamta jest wyżej).
 * Dachy skrzydeł nie są docinane do siebie, więc bez tego moduły dobudówki trafiały pod okap albo do wnętrza
 * domu. Zapas to odstęp modułów od krawędzi (`margines`).
 */
const podDachemInnych = (bryla, polac, margines) => {
  const wynik = []
  const rzut = rzutPolaci(polac)
  for (const q of bryla.polacie || []) {
    if (q.skrzydloId === polac.skrzydloId) continue
    let w = przytnijDoWypuklego(rzutPolaci(q), rzut)
    if (!w.length) continue
    // q wyżej niż połać: y_q − y_p > próg, różnica płaszczyzn jest liniowa w (x, z).
    const f = (x, z) => (yPlaszczyzny(q, x, z) ?? -Infinity) - (yPlaszczyzny(polac, x, z) ?? Infinity) - PROG_ZAKRYCIA
    const f0 = f(0, 0)
    const ax = f(1, 0) - f0
    const az = f(0, 1) - f0
    if (!Number.isFinite(f0)) continue
    w = przytnijPolplaszczyzna(przeciwnieDoZegara(w), ax, az, -f0)
    if (!w.length || Math.abs(pole2D(w)) < 1e-4) continue
    const obrys = w.map(([x, z]) => uvPolaci(polac, naPlaszczyznie(polac, x, z)))
    const x = dlaPolaci(polac, 'pod_dachem', `pod_dachem:${polac.id}:${q.id}`, null, obrys, margines, 0)
    if (x) wynik.push(x)
  }
  return wynik
}

/**
 * Wszystkie przeszkody połaci: elementów na dachu i części pod wyższym dachem innego skrzydła.
 * @returns [{ id, elementId, rodzaj, nazwa, polacId, obrys, strefa, zapas, wysokosc, strefaNaDachu }]
 */
export const przeszkodyNaPolaci = ({ bryla, elementy = [], polac, margines = 0.3 }) => [
  ...przeszkodyElementow(bryla, elementy, polac),
  ...podDachemInnych(bryla, polac, margines),
]

/**
 * Pionowa bryła przeszkody stojącej w scenie (do widoku i cieni): od 0,6 m pod dachem (komin) albo od dachu
 * (z zagłębieniem, żeby na stromej połaci dolna krawędź nie wisiała nad pokryciem) do wysokości ponad dach
 * w środku podstawy.
 */
export const brylaStojacej = (bryla, el) => {
  const d = parametryElementu(el)
  const [x, , z] = el.pozycja
  const yd = wysokoscDachu(bryla, x, z) ?? 3
  const komin = el.typ === 'komin'
  const gora = yd + (komin ? (d.ponadDach ?? 1.0) : (d.wysokosc ?? 0.3))
  const pod = polacPod(bryla, x, z)
  const zaglebienie = 0.05 + (Math.max(d.szerokosc ?? 0.5, d.glebokosc ?? 0.5) / 2) * Math.tan(Math.min(80, pod?.nachylenie ?? 0) * RAD)
  const dol = komin ? Math.min(yd - 0.6, yd - zaglebienie) : yd - zaglebienie
  return {
    srodek: [x, (gora + dol) / 2, z], wymiary: [d.szerokosc ?? 0.5, gora - dol, d.glebokosc ?? 0.5],
    obrot: el.obrot || 0, gora, yDachu: yd,
  }
}
